import type { PrintTemplate } from './print-templates';
import type { CreditNoteKind, CreditNoteStatus } from './receivables';

/**
 * The business identity a printed document shows.
 *
 * `SNAPSHOT`: frozen when the document was issued, whatever the tenant's profile says now. `LEGACY`: the
 * document predates frozen identity, so it shows the tenant's system name and the branch as it is now,
 * exactly as it always did, and never branding that was not recorded with it.
 */
export interface PrintedBusinessIdentity {
  source: 'SNAPSHOT' | 'LEGACY';
  displayName: string;
  legalName: string | null;
  taxRegistrationNumber: string | null;
  /** A short-lived signed URL to the logo frozen with the document, never the tenant's current logo. */
  logo: { url: string; width: number; height: number } | null;
}
/**
 * Sales contracts shared between the API and the counter.
 *
 * Three quantities on every line, never one. `quantityStockIssued` is what has left sellable
 * inventory; `quantityDelivered` is what the customer physically holds. A frame at the lab has the
 * first without the second, and a single field cannot answer both questions.
 */

import type { PdType } from './measurements';
import { CreditInvoiceIssuePolicy, CreditLimitPolicy } from './receivables';

export const TaxTreatment = {
  STANDARD: 'STANDARD',
  ZERO_RATED: 'ZERO_RATED',
  EXEMPT: 'EXEMPT',
} as const;
export type TaxTreatment = (typeof TaxTreatment)[keyof typeof TaxTreatment];

/** When the fiscal document is raised. The tax point differs by country, so it is policy. */
export const InvoiceIssuePolicy = {
  ON_CONFIRMATION: 'ON_CONFIRMATION',
  ON_FIRST_PAYMENT: 'ON_FIRST_PAYMENT',
  ON_FULL_PAYMENT: 'ON_FULL_PAYMENT',
  ON_FIRST_DELIVERY: 'ON_FIRST_DELIVERY',
  MANUAL: 'MANUAL',
} as const;
export type InvoiceIssuePolicy = (typeof InvoiceIssuePolicy)[keyof typeof InvoiceIssuePolicy];

export const RefundTenderPolicy = {
  REQUIRE_ORIGINAL: 'REQUIRE_ORIGINAL',
  WARN_ON_MISMATCH: 'WARN_ON_MISMATCH',
  ALLOW_DIFFERENT: 'ALLOW_DIFFERENT',
} as const;
export type RefundTenderPolicy = (typeof RefundTenderPolicy)[keyof typeof RefundTenderPolicy];

export const SalesOrderStatus = {
  DRAFT: 'DRAFT',
  CONFIRMED: 'CONFIRMED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;
export type SalesOrderStatus = (typeof SalesOrderStatus)[keyof typeof SalesOrderStatus];

/** Where an optical job physically is. Declared now; exercised in Phase 3B. */
export const SalesLineWorkflowStatus = {
  NOT_STARTED: 'NOT_STARTED',
  WAITING_FOR_LENS: 'WAITING_FOR_LENS',
  SENT_TO_LAB: 'SENT_TO_LAB',
  IN_PRODUCTION: 'IN_PRODUCTION',
  RECEIVED_FROM_LAB: 'RECEIVED_FROM_LAB',
  QC: 'QC',
  READY_FOR_COLLECTION: 'READY_FOR_COLLECTION',
  CUSTOMER_NOTIFIED: 'CUSTOMER_NOTIFIED',
  DELIVERED: 'DELIVERED',
} as const;
export type SalesLineWorkflowStatus =
  (typeof SalesLineWorkflowStatus)[keyof typeof SalesLineWorkflowStatus];

/**
 * How a line reaches the customer.
 *
 * A lens cloth is handed over at the till. A frame chosen for glazing leaves sellable stock the
 * same afternoon but goes to the lab, so it is issued and not delivered. A prescription lens does
 * not exist yet, so there is nothing to issue until it arrives.
 */
export const SalesLineFulfilmentMode = {
  IMMEDIATE: 'IMMEDIATE',
  DEFERRED_COLLECTION: 'DEFERRED_COLLECTION',
  DEFERRED_SUPPLY: 'DEFERRED_SUPPLY',
} as const;
export type SalesLineFulfilmentMode =
  (typeof SalesLineFulfilmentMode)[keyof typeof SalesLineFulfilmentMode];

/** True for anything the customer does not walk out with. */
export function isDeferredMode(mode: SalesLineFulfilmentMode): boolean {
  return mode !== SalesLineFulfilmentMode.IMMEDIATE;
}

export const PaymentTransactionType = { PAYMENT: 'PAYMENT', REFUND: 'REFUND' } as const;
export type PaymentTransactionType =
  (typeof PaymentTransactionType)[keyof typeof PaymentTransactionType];

export const PaymentMethod = {
  CASH: 'CASH',
  CARD: 'CARD',
  BANK_TRANSFER: 'BANK_TRANSFER',
  INSTAPAY: 'INSTAPAY',
  WALLET: 'WALLET',
  OTHER: 'OTHER',
} as const;
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

export const InvoiceStatus = { ISSUED: 'ISSUED', VOID: 'VOID' } as const;
export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];

/** What has left sellable stock. Derived; never stored. */
export const StockIssueStatus = {
  NOT_ISSUED: 'NOT_ISSUED',
  PARTIALLY_ISSUED: 'PARTIALLY_ISSUED',
  FULLY_ISSUED: 'FULLY_ISSUED',
} as const;
export type StockIssueStatus = (typeof StockIssueStatus)[keyof typeof StockIssueStatus];

/** What the customer physically holds. Derived from delivery alone — stock issue does not feed it. */
export const FulfilmentStatus = {
  NOT_DELIVERED: 'NOT_DELIVERED',
  PARTIALLY_DELIVERED: 'PARTIALLY_DELIVERED',
  DELIVERED: 'DELIVERED',
} as const;
export type FulfilmentStatus = (typeof FulfilmentStatus)[keyof typeof FulfilmentStatus];

export const OrderPaymentStatus = {
  UNPAID: 'UNPAID',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  PAID: 'PAID',
  PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
  REFUNDED: 'REFUNDED',
} as const;
export type OrderPaymentStatus = (typeof OrderPaymentStatus)[keyof typeof OrderPaymentStatus];

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export interface TaxProfileSummary {
  id: string;
  code: string;
  nameEn: string;
  nameAr: string | null;
  rateBasisPoints: number;
  treatment: TaxTreatment;
  pricesIncludeTax: boolean;
  isDefault: boolean;
  isActive: boolean;
}

export interface SalesSettingsSummary {
  invoiceIssuePolicy: InvoiceIssuePolicy;
  refundTenderPolicy: RefundTenderPolicy;
  maxDiscountPercent: number;
  requireCustomerForPrescription: boolean;

  /** Credit is opt-in; everything below only matters once it is on. */
  allowCustomerCredit: boolean;
  defaultCreditDays: number;
  maxInstallments: number;
  defaultCreditLimitMinor: number;
  creditLimitPolicy: CreditLimitPolicy;
  creditInvoiceIssuePolicy: CreditInvoiceIssuePolicy;
}

export const DEFAULT_SALES_SETTINGS: SalesSettingsSummary = {
  invoiceIssuePolicy: InvoiceIssuePolicy.ON_CONFIRMATION,
  refundTenderPolicy: RefundTenderPolicy.WARN_ON_MISMATCH,
  maxDiscountPercent: 10,
  requireCustomerForPrescription: true,
  // Two switches, both off: a shop that has never given credit cannot give it by accident.
  allowCustomerCredit: false,
  defaultCreditDays: 30,
  maxInstallments: 12,
  defaultCreditLimitMinor: 0,
  creditLimitPolicy: CreditLimitPolicy.BLOCK,
  creditInvoiceIssuePolicy: CreditInvoiceIssuePolicy.ON_CONFIRMATION,
};

/**
 * Tax profiles every tenant starts with.
 *
 * Egypt's standard rate is the default because that is where the first tenants are; a tenant in
 * another market edits the rate rather than discovering an empty list at the counter.
 */
export const DEFAULT_TAX_PROFILES: readonly {
  code: string;
  nameEn: string;
  nameAr: string;
  rateBasisPoints: number;
  treatment: TaxTreatment;
  pricesIncludeTax: boolean;
  isDefault: boolean;
}[] = [
  {
    code: 'STANDARD',
    nameEn: 'Standard rate',
    nameAr: 'النسبة القياسية',
    rateBasisPoints: 1400,
    treatment: TaxTreatment.STANDARD,
    pricesIncludeTax: true,
    isDefault: true,
  },
  {
    code: 'ZERO',
    nameEn: 'Zero-rated',
    nameAr: 'نسبة صفرية',
    rateBasisPoints: 0,
    treatment: TaxTreatment.ZERO_RATED,
    pricesIncludeTax: true,
    isDefault: false,
  },
  {
    code: 'EXEMPT',
    nameEn: 'Exempt',
    nameAr: 'معفى',
    rateBasisPoints: 0,
    treatment: TaxTreatment.EXEMPT,
    pricesIncludeTax: true,
    isDefault: false,
  },
];

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

export interface SalesOrderLineSummary {
  id: string;
  variantId: string;
  sku: string;
  productName: string;
  variantName: string | null;
  isBatchTracked: boolean;

  quantityOrdered: number;
  /** Left sellable stock. Drives the ledger. */
  quantityStockIssued: number;
  /** In the customer's hands. Drives fulfilment. */
  quantityDelivered: number;
  /** `quantityStockIssued − quantityDelivered`: with the business, not the customer, not sellable. */
  quantityCommittedNotDelivered: number;

  unitPriceMinor: number;
  discountMinor: number;
  netMinor: number;
  taxMinor: number;
  totalMinor: number;

  taxProfileId: string | null;
  taxTreatment: TaxTreatment;
  taxRateBasisPoints: number;

  fulfilmentMode: SalesLineFulfilmentMode;
  /** `J1`, `J2`… for a deferred line; null for anything handed over at the till. */
  jobRef: string | null;
  workflowStatus: SalesLineWorkflowStatus;
  /** The optical job this line belongs to, if any. */
  opticalJobId: string | null;
  allocations: { batchId: string; batchNumber: string; quantity: number }[];
  notes: string | null;
}

export interface SalesOrderSummary {
  id: string;
  reference: string;
  status: SalesOrderStatus;
  branchId: string;
  branchName: string;
  customerId: string | null;
  customerName: string | null;

  currencyCode: string;
  subtotalMinor: number;
  discountMinor: number;
  taxMinor: number;
  totalMinor: number;

  paidMinor: number;
  refundedMinor: number;
  /** `totalMinor − (paid − refunded)`. Negative would mean an overpayment, which is refused. */
  balanceMinor: number;

  stockIssueStatus: StockIssueStatus;
  fulfilmentStatus: FulfilmentStatus;
  paymentStatus: OrderPaymentStatus;

  lineCount: number;
  invoiceCount: number;
  /** True when any line is collected later rather than handed over at the till. */
  hasDeferredLines: boolean;

  confirmedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelledReason: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SalesOrderDetail extends SalesOrderSummary {
  lines: SalesOrderLineSummary[];
  payments: PaymentTransactionSummary[];
  invoices: InvoiceSummary[];
  notes: string | null;
}

// ---------------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------------

export interface PaymentTransactionSummary {
  id: string;
  type: PaymentTransactionType;
  method: PaymentMethod;
  /** Always positive. `type` carries the direction. */
  amountMinor: number;
  currencyCode: string;
  reference: string | null;
  originalPaymentTransactionId: string | null;
  processedByName: string | null;
  occurredAt: string;
}

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

export interface InvoiceLineSummary {
  id: string;
  description: string;
  sku: string | null;
  quantity: number;
  unitPriceMinor: number;
  discountMinor: number;
  netMinor: number;
  taxMinor: number;
  totalMinor: number;
  taxTreatment: TaxTreatment;
  taxRateBasisPoints: number;
}

export interface InvoiceSummary {
  id: string;
  reference: string;
  status: InvoiceStatus;
  orderId: string;
  orderReference: string;
  currencyCode: string;
  subtotalMinor: number;
  discountMinor: number;
  taxMinor: number;
  totalMinor: number;
  customerNameSnapshot: string | null;
  issuedAt: string;
  issuedByName: string | null;
}

/** An invoice as the invoice list shows it: the summary plus its branch and its order's state. */
export interface InvoiceListItem extends InvoiceSummary {
  branchName: string;
  /** True when the order the invoice was raised for has since been cancelled. */
  orderCancelled: boolean;
  /**
   * The settlement of the invoice's order, from the canonical order receivable — never a settlement of
   * the invoice itself, which the domain does not track. Null for a cancelled order, exactly as the
   * public verification page shows it.
   */
  orderPaymentStatus: VerifiedOrderPaymentStatus | null;
}

/** A credit note that offsets this invoice, as the invoice page lists it. */
export interface InvoiceAdjustment {
  id: string;
  reference: string;
  kind: CreditNoteKind;
  status: CreditNoteStatus;
  issuedAt: string;
  currencyCode: string;
  totalMinor: number;
}

export interface InvoiceDetail extends InvoiceListItem {
  lines: InvoiceLineSummary[];
  /** The order's money from the canonical order receivable; null for a cancelled order. Never per-invoice. */
  orderPayment: { netCollectedMinor: number; outstandingMinor: number } | null;
  /** Issued and voided credit notes against this invoice, newest first. */
  adjustments: InvoiceAdjustment[];
  /** What issued credit notes have done to the document: the same rule the verification page uses. */
  notice: VerifiedDocumentNotice | null;
}

/** Where the invoice's own verification page lives; fetched only when someone asks to open it. */
export interface InvoiceVerificationLink {
  url: string;
}

// ---------------------------------------------------------------------------
// Optical workflow
// ---------------------------------------------------------------------------

/**
 * Which workflow states may follow which.
 *
 * Written out rather than inferred from the enum's order, because the real flow is not a straight
 * line: a job can fail QC and go back to the lab, and a customer can be notified more than once.
 * An explicit map makes an impossible transition a rejection instead of a silent state change.
 */
export const ALLOWED_WORKFLOW_TRANSITIONS: Readonly<
  Record<SalesLineWorkflowStatus, readonly SalesLineWorkflowStatus[]>
> = {
  NOT_STARTED: ['WAITING_FOR_LENS', 'SENT_TO_LAB'],
  WAITING_FOR_LENS: ['SENT_TO_LAB'],
  SENT_TO_LAB: ['IN_PRODUCTION', 'RECEIVED_FROM_LAB'],
  IN_PRODUCTION: ['RECEIVED_FROM_LAB'],
  RECEIVED_FROM_LAB: ['QC'],
  // Failing QC sends the job back to the lab; that is the common case, not an exception.
  QC: ['READY_FOR_COLLECTION', 'SENT_TO_LAB'],
  READY_FOR_COLLECTION: ['CUSTOMER_NOTIFIED', 'DELIVERED'],
  // A customer can be told more than once, and can also just walk in.
  CUSTOMER_NOTIFIED: ['CUSTOMER_NOTIFIED', 'DELIVERED'],
  DELIVERED: [],
};

export function canTransitionWorkflow(
  from: SalesLineWorkflowStatus,
  to: SalesLineWorkflowStatus,
): boolean {
  return (ALLOWED_WORKFLOW_TRANSITIONS[from] ?? []).includes(to);
}

/** States in which the goods are physically back and waiting for the customer. */
export const READY_STATES: readonly SalesLineWorkflowStatus[] = [
  'READY_FOR_COLLECTION',
  'CUSTOMER_NOTIFIED',
];

export const PrescriptionSource = {
  MANUAL: 'MANUAL',
  MEASUREMENT_SESSION: 'MEASUREMENT_SESSION',
  /** Written outside this business and typed for one optical job. */
  EXTERNAL_PRESCRIPTION: 'EXTERNAL_PRESCRIPTION',
  /** An exact copy of an issued clinical prescription of the order's own customer, named by its id. */
  CLINICAL_PRESCRIPTION: 'CLINICAL_PRESCRIPTION',
} as const;
export type PrescriptionSource = (typeof PrescriptionSource)[keyof typeof PrescriptionSource];

export const PrismBase = { UP: 'UP', DOWN: 'DOWN', IN: 'IN', OUT: 'OUT' } as const;
export type PrismBase = (typeof PrismBase)[keyof typeof PrismBase];

/** One eye of a prescription. Values are decimal strings, never floats. */
export interface EyePrescription {
  sphere: string | null;
  cylinder: string | null;
  axis: number | null;
  add: string | null;
  prism: string | null;
  prismBase: PrismBase | null;
}

export interface PrescriptionSummary {
  id: string;
  jobRef: string | null;
  right: EyePrescription;
  left: EyePrescription;
  rightPdMm: string | null;
  leftPdMm: string | null;
  totalPdMm: string | null;
  /** Snapshotted with the values, so an old order still says which PD it holds. Null only when there is no PD. */
  pdType: PdType | null;
  source: PrescriptionSource;
  /** The measurement the PD was copied from, so a re-save can carry the trace forward. Null when typed by hand. */
  sourceMeasurementId: string | null;
  notes: string | null;
  recordedByName: string | null;
  updatedAt: string;
}

/** A group of lines that travel to the lab together. Derived from `jobRef`, not stored. */
export interface OpticalJobSummary {
  jobRef: string;
  workflowStatus: SalesLineWorkflowStatus;
  lineIds: string[];
  /** True once every line in the job has reached the customer. */
  isDelivered: boolean;
  hasPrescription: boolean;
}

/** Dioptric powers are prescribed in quarter-dioptre steps; anything else is a typo. */
export const DIOPTRE_STEP = 0.25;

export function isValidDioptre(value: number): boolean {
  return (
    Number.isFinite(value) &&
    Math.abs(Math.round(value / DIOPTRE_STEP) * DIOPTRE_STEP - value) < 1e-9
  );
}

// ---------------------------------------------------------------------------
// Returns
// ---------------------------------------------------------------------------

/**
 * Where returned goods physically went.
 *
 * New returns default to QUARANTINE and stay outside sellable stock until someone has looked at
 * them. Putting a returned frame straight back on the shelf is how a scratched lens gets sold
 * twice.
 */
export const ReturnDisposition = {
  QUARANTINE: 'QUARANTINE',
  RESTOCK: 'RESTOCK',
  DAMAGED: 'DAMAGED',
  DISPOSED: 'DISPOSED',
} as const;
export type ReturnDisposition = (typeof ReturnDisposition)[keyof typeof ReturnDisposition];

export const SalesReturnStatus = { OPEN: 'OPEN', CANCELLED: 'CANCELLED' } as const;
export type SalesReturnStatus = (typeof SalesReturnStatus)[keyof typeof SalesReturnStatus];

/** Dispositions that put nothing back on the shelf. */
export const NON_SELLABLE_DISPOSITIONS: readonly ReturnDisposition[] = [
  'QUARANTINE',
  'DAMAGED',
  'DISPOSED',
];

export interface SalesReturnLineSummary {
  id: string;
  orderLineId: string;
  variantId: string;
  sku: string;
  productName: string;
  quantity: number;
  disposition: ReturnDisposition;
  batchId: string | null;
  batchNumber: string | null;
  inspectedAt: string | null;
  inspectedByName: string | null;
  notes: string | null;
}

export interface SalesReturnSummary {
  id: string;
  reference: string;
  status: SalesReturnStatus;
  orderId: string;
  orderReference: string;
  branchId: string;
  branchName: string;
  customerName: string | null;
  lineCount: number;
  totalQuantity: number;
  /** Lines still awaiting inspection. */
  quarantinedQuantity: number;
  reason: string | null;
  createdByName: string | null;
  createdAt: string;
}

export interface SalesReturnDetail extends SalesReturnSummary {
  lines: SalesReturnLineSummary[];
  notes: string | null;
  cancelledReason: string | null;
}

/** How much of a delivered line may still be returned. */
export interface ReturnableLine {
  orderLineId: string;
  variantId: string;
  sku: string;
  productName: string;
  quantityDelivered: number;
  quantityReturned: number;
  /** `quantityDelivered − quantityReturned`. Zero for anything never handed over. */
  returnableQuantity: number;
  batchId: string | null;
}

/**
 * What the branch is physically holding, in the three states it can be in.
 *
 * Each figure is a query over rows that exist for their own reasons, so they cannot drift apart the
 * way a second balance table would.
 */
export interface CustodyReport {
  branchId: string;
  branchName: string;
  /** On the shelf and available to sell. */
  sellableQuantity: number;
  /** Left sellable stock but not yet with the customer — at the lab, on the collection shelf. */
  issuedNotDeliveredQuantity: number;
  /** Returned by customers and awaiting inspection. */
  quarantinedQuantity: number;
  /** The sum: everything physically under the business's control. */
  totalUnderCustodyQuantity: number;
}

/** What changed about one basket line since the counter last looked. */
export const BasketLineIssue = {
  /** The variant no longer exists, or the tenant cannot see it. */
  UNAVAILABLE: 'UNAVAILABLE',
  /** Discontinued, or otherwise not sellable. */
  INACTIVE: 'INACTIVE',
  /** The catalogue price is no longer what the basket believes. */
  PRICE_CHANGED: 'PRICE_CHANGED',
  /** Not enough sellable stock at this branch, expired lots excluded. */
  INSUFFICIENT_STOCK: 'INSUFFICIENT_STOCK',
} as const;
export type BasketLineIssue = (typeof BasketLineIssue)[keyof typeof BasketLineIssue];

export interface ValidatedBasketLine {
  /** The key the caller sent with this line, unchanged, or null when it sent none. One result per request line, in request order, each with its own key. */
  lineKey: string | null;
  variantId: string;
  sku: string;
  productName: string;
  quantity: number;
  /** What the client thought the unit price was, when it said. */
  expectedUnitPriceMinor: number | null;
  unitPriceMinor: number;
  availableQuantity: number;
  issues: BasketLineIssue[];
}

/** Issues that concern the customer rather than a line. */
export const BasketCustomerIssue = {
  MISSING: 'MISSING',
  INACTIVE: 'INACTIVE',
  CREDIT_UNAVAILABLE: 'CREDIT_UNAVAILABLE',
  CREDIT_LIMIT_EXCEEDED: 'CREDIT_LIMIT_EXCEEDED',
} as const;
export type BasketCustomerIssue = (typeof BasketCustomerIssue)[keyof typeof BasketCustomerIssue];

/** The refusal the checkout would give the pairs of a basket right now: its error code, message and every field it names. Never a prescription value. */
export interface BasketOpticalRefusal {
  code: string;
  message: string;
  details: { field?: string; message: string }[];
}

/** What the checkout's pair rules say about the pairs a basket names, read without writing or holding anything. */
export interface BasketOpticalCheck {
  /** Null when the pairs pass every rule the checkout holds them to as things stand. */
  refusal: BasketOpticalRefusal | null;
}

/**
 * A price check, not a reservation.
 *
 * Nothing here is held for the caller. Stock can move between this answer and the checkout, which
 * is exactly why the checkout revalidates inside its own transaction rather than trusting this.
 */
export interface BasketValidation {
  currencyCode: string;
  lines: ValidatedBasketLine[];
  customerIssues: BasketCustomerIssue[];
  /** Recomputed from the current catalogue and tax profiles. */
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  /** True when nothing material moved and the basket can go straight to checkout. */
  isUnchanged: boolean;
  /** Present only when the basket names pairs of spectacles. A line of a pair is then checked for stock as the checkout fulfils it: a made-to-order lens needs none, and the frame and stocked lenses are counted together per variant. */
  optical?: BasketOpticalCheck;
}

/** Everything a receipt or invoice needs, in one read. */
export interface PrintableDocument {
  kind: 'RECEIPT' | 'INVOICE';
  tenantName: string;
  branchName: string;
  branchPhone: string | null;
  branchAddress: string | null;
  reference: string;
  /** Already formatted in the tenant's timezone; the print page does no date maths. */
  issuedAt: string;
  customerName: string | null;
  cashierName: string | null;
  currencyCode: string;
  lines: {
    description: string;
    sku: string | null;
    quantity: number;
    unitPriceMinor: number;
    discountMinor: number;
    taxMinor: number;
    totalMinor: number;
  }[];
  subtotalMinor: number;
  discountMinor: number;
  taxMinor: number;
  totalMinor: number;
  payments: { method: string; amountMinor: number }[];
  paidMinor: number;
  outstandingMinor: number;
  /** The document's permanent verification QR: the URL it encodes and the symbol, drawn by the server as SVG. The same for every reprint. */
  verification: { url: string; qrSvg: string };
  /** How this tenant prints this document type: its saved template or the system default. Presentation only; nothing above depends on it. */
  template: PrintTemplate;
  /** Who issued the document, as frozen at issuance; `tenantName`, `branchName`, `branchPhone` and `branchAddress` above are resolved from it. */
  identity: PrintedBusinessIdentity;
}

export const VerifiedDocumentStatus = {
  VALID: 'VALID',
  VOID: 'VOID',
  CANCELLED: 'CANCELLED',
} as const;
export type VerifiedDocumentStatus =
  (typeof VerifiedDocumentStatus)[keyof typeof VerifiedDocumentStatus];

/** The order's settlement in words, from the canonical order receivable. */
export const VerifiedOrderPaymentStatus = {
  PAID: 'PAID',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  UNPAID: 'UNPAID',
} as const;
export type VerifiedOrderPaymentStatus =
  (typeof VerifiedOrderPaymentStatus)[keyof typeof VerifiedOrderPaymentStatus];

/** What issued credit notes have done to the document since it was printed. */
export const VerifiedDocumentNotice = {
  PARTIALLY_RETURNED: 'PARTIALLY_RETURNED',
  RETURNED: 'RETURNED',
  CREDITED: 'CREDITED',
} as const;
export type VerifiedDocumentNotice =
  (typeof VerifiedDocumentNotice)[keyof typeof VerifiedDocumentNotice];

/**
 * What a scanned invoice or receipt QR reveals, and all of it.
 *
 * Public to anyone holding the paper, so the list is the design: enough to confirm the document is
 * genuine and where it stands now, and nothing that identifies the customer or what they bought. No
 * customer, no product names, no prescription, no payment method or reference, no identifiers. The
 * payment status is the order's, because one order may carry several invoices.
 */
export interface PublicDocumentVerificationView {
  kind: 'INVOICE' | 'RECEIPT';
  /** The business number printed on the document: INV-… or SO-…. */
  reference: string;
  storeName: string;
  branchName: string;
  /** The document's date in its branch's own timezone, YYYY-MM-DD. */
  issuedOn: string;
  currencyCode: string;
  totalMinor: number;
  status: VerifiedDocumentStatus;
  /** Null for a cancelled order: it is no longer a sale anyone pays, and the canonical receivables leave it out. */
  orderPaymentStatus: VerifiedOrderPaymentStatus | null;
  notice: VerifiedDocumentNotice | null;
  itemCount: number;
}

/** Where a signed-in member of staff can open a verified document inside OptiFlow. */
export interface VerifiedDocumentLocation {
  path: string;
}
